import { useState, useEffect } from "react";
import { Cookie, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

/**
 * CookieConsent — bannière RGPD-conforme (CNIL 2020 + ePrivacy).
 *
 * LES-LEX FR n'utilise QUE des cookies strictement nécessaires,
 * donc pas d'obligation d'obtenir un consentement opt-in.
 * La bannière informe l'utilisateur de leur présence et donne accès
 * à la politique cookies (conformité art. 82 Loi Inf. et Libertés).
 *
 * L'utilisateur peut fermer la bannière (acknowledgment) — son choix
 * est stocké dans localStorage pour ne plus l'afficher.
 */
export default function CookieConsent() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    try {
      const acknowledged = localStorage.getItem("les_lex_cookie_ack");
      if (!acknowledged) {
        // Show after short delay to not interrupt first impressions
        const t = setTimeout(() => setShow(true), 1500);
        return () => clearTimeout(t);
      }
    } catch {
      setShow(true);
    }
  }, []);

  const acknowledge = () => {
    try {
      localStorage.setItem("les_lex_cookie_ack", new Date().toISOString());
    } catch {}
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-[100] bg-background border-2 border-primary/30 shadow-2xl rounded-lg p-4 animate-in slide-in-from-bottom-4"
      role="dialog"
      aria-labelledby="cookie-title"
    >
      <div className="flex items-start gap-3">
        <Cookie className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
        <div className="flex-1 text-sm">
          <h3 id="cookie-title" className="font-semibold mb-1">Cookies essentiels</h3>
          <p className="text-muted-foreground text-xs leading-relaxed">
            LES-LEX FR utilise uniquement des cookies <strong>strictement nécessaires</strong> à son fonctionnement
            (authentification, préférence de langue). <strong>Aucun cookie publicitaire ni de tracking.</strong>
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button size="sm" onClick={acknowledge} className="text-xs">
              J'ai compris
            </Button>
            <Link to="/cookies" className="text-xs text-primary hover:underline">
              En savoir plus
            </Link>
          </div>
        </div>
        <button
          onClick={acknowledge}
          className="text-muted-foreground hover:text-foreground"
          aria-label="Fermer la bannière cookies"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
